import { AxiosResponse } from 'axios'
import { api, handleError } from 'API'
import { PracticeEventStatus } from 'configs/constants/enums/schedule'

export interface IPracticeEvent {
  id?: number
  name: string
  description?: string
  scheduledDate?: Date | string
  startTime?: Date | string
  endTime?: Date | string
  duration?: number
  status?: PracticeEventStatus
  location?: string
  note?: string
  /** Seasonal Teams providing this event's context, roster and calendar. */
  teamIds?: number[]
  /** Player Groups used to pick the participants. Provenance, not the roster. */
  playerGroupIds?: number[]
  sharedWithPlayers?: boolean
  lineBalanceId?: number
  organizationId?: number
  createdBy?: number
  lineBalance?: any
  /** Seasonal Teams providing this event's context, resolved by the BE. */
  teams?: Array<{ id: number; name: string }>
  creator?: {
    id?: number
    firstName?: string
    lastName?: string
    image?: string
  }
  periods?: Array<{
    id?: number
    name: string
    duration?: number
    position?: number
  }>
  coaches?: Array<{
    id: number
    firstName?: string
    lastName?: string
    image?: string
    initials?: string
  }>
  users?: Array<{
    id: number
    firstName?: string
    lastName?: string
    image?: string
    initials?: string
  }>
}

export interface CreatePracticeEventDto {
  lineBalanceId: number
  scheduledDate: Date | string
  startTime: Date | string
  location?: string
  /**
   * Seasonal Team context (§7.1 step 1). Replaces the old singular `teamId`,
   * which held a Player Group id — the BE rejects that field with a 400.
   */
  teamIds?: number[]
  /** Player Groups used to select participants within the eligible roster. */
  playerGroupIds?: number[]
  note?: string
  status?: PracticeEventStatus
}

export interface PracticeEventFilterDto {
  /** BE validator requires organizationId on the search body. */
  organizationId?: number
  page?: number
  pageSize?: number
  status?: PracticeEventStatus
  statuses?: number[]
  /**
   * Filter by seasonal Team. Previously this name meant Player Group ids; it now
   * means real Teams. The org scope still rides on the `?orgId=` query param.
   */
  teamIds?: number[]
  /** Filter by the Player Groups used to build the participant list. */
  playerGroupIds?: number[]
  lineBalanceId?: number
  lineBalanceIds?: number[]
  assigneeMembershipIds?: number[]
  assignmentStatus?: 'has' | 'none'
  scheduledDateFrom?: Date | string
  scheduledDateTo?: Date | string
  sortBy?: string
  search?: string
}

export async function createPracticeEvent(data: CreatePracticeEventDto, orgId?: number): Promise<IPracticeEvent> {
  try {
    const response: AxiosResponse = await api.post(
      '/practice-events',
      data,
      orgId != null ? { params: { orgId } } : undefined
    )
    return response.data
  } catch (error: any) {
    handleError(error as Error, 'API/practice-event/index.ts', 'createPracticeEvent')
    throw error
  }
}

export async function searchPracticeEvents(
  filterDto: PracticeEventFilterDto,
  orgId?: number
): Promise<{ results: IPracticeEvent[]; totalCount: number }> {
  try {
    // orgId (query) scopes the call to one organization, overriding the
    // interceptor's global stamp. BE resolves org scope from `?orgId=`, not the body.
    const response: AxiosResponse = await api.post(
      '/practice-events/search',
      filterDto,
      orgId != null ? { params: { orgId } } : undefined
    )
    return response.data
  } catch (error: any) {
    handleError(error as Error, 'API/practice-event/index.ts', 'searchPracticeEvents')
    return { results: [], totalCount: 0 }
  }
}

export async function getPracticeEvents(
  params: {
    page?: number
    pageSize?: number
  },
  orgId?: number
): Promise<{ results: IPracticeEvent[]; totalCount: number }> {
  try {
    // orgId scopes the call to one organization, overriding the interceptor's stamp.
    const response: AxiosResponse = await api.get('/practice-events', {
      params: orgId != null ? { ...params, orgId } : params
    })
    return response.data
  } catch (error: any) {
    handleError(error as Error, 'API/practice-event/index.ts', 'getPracticeEvents')
    return { results: [], totalCount: 0 }
  }
}

export async function getPracticeEventById(id: number): Promise<IPracticeEvent> {
  try {
    const response: AxiosResponse = await api.get(`/practice-events/${id}`)
    return response.data
  } catch (error: any) {
    handleError(error as Error, 'API/practice-event/index.ts', 'getPracticeEventById')
    return {} as IPracticeEvent
  }
}

export async function updatePracticeEvent(
  id: number,
  data: Partial<IPracticeEvent>,
  orgId?: number
): Promise<IPracticeEvent> {
  try {
    // orgId scopes the by-id op to the owning organization — BE resolves ownership
    // from the `?orgId=` query, so a mutation under a different active org 404s.
    const response: AxiosResponse = await api.patch(
      `/practice-events/${id}`,
      data,
      orgId != null ? { params: { orgId } } : undefined
    )
    return response.data
  } catch (error: any) {
    handleError(error as Error, 'API/practice-event/index.ts', 'updatePracticeEvent')
    throw error
  }
}

export async function deletePracticeEvent(id: number, orgId?: number): Promise<void> {
  try {
    await api.delete(`/practice-events/${id}`, orgId != null ? { params: { orgId } } : undefined)
  } catch (error: any) {
    handleError(error as Error, 'API/practice-event/index.ts', 'deletePracticeEvent')
    throw error
  }
}

export interface AssigneeDto {
  membershipId: number
  assigneeType: 'player' | 'coach'
  /**
   * Player Group this pick came from; omit when the member was picked individually.
   * The BE uses it to freeze the rest of (event Teams ∩ this Group) as the event's
   * audience, so group-mates who were not ticked still see the session.
   */
  groupId?: number
}

export interface StationAssigneeDto extends AssigneeDto {
  workStationId: number
}

export interface SegmentAssigneeDto extends AssigneeDto {
  practiceSegmentId: number
}

export interface LaneAssigneeDto extends AssigneeDto {
  practiceLaneId: number
}

export interface UpdateAssigneesDto {
  eventAssignees?: AssigneeDto[]
  laneAssignees?: LaneAssigneeDto[]
  segmentAssignees?: SegmentAssigneeDto[]
  stationAssignees?: StationAssigneeDto[]
  removeEventAssignees?: AssigneeDto[]
  removeLaneAssignees?: LaneAssigneeDto[]
  removeSegmentAssignees?: SegmentAssigneeDto[]
  removeStationAssignees?: StationAssigneeDto[]
}

export async function updatePracticeEventAssignees(eventId: number, dto: UpdateAssigneesDto): Promise<void> {
  try {
    const response: AxiosResponse = await api.put(`/practice-events/${eventId}/assignees`, dto)
    return response.data
  } catch (error: any) {
    handleError(error as Error, 'API/practice-event/index.ts', 'updatePracticeEventAssignees')
    throw error
  }
}

export async function duplicatePracticeEvent(id: number, name?: string, orgId?: number): Promise<IPracticeEvent> {
  try {
    const response: AxiosResponse = await api.post(
      `/practice-events/${id}/duplicate`,
      { name },
      orgId != null ? { params: { orgId } } : undefined
    )
    return response.data.data
  } catch (error: any) {
    handleError(error as Error, 'API/practice-event/index.ts', 'duplicatePracticeEvent')
    throw error
  }
}
